/**
 * useWebSocket
 * Connects after context is ready. Watches contextToken: connect when present,
 * reinitialize when it changes, disconnect when removed.
 *
 * In React 18 Strict Mode (dev), the effect runs twice and cleanup runs in between,
 * so you may see a one-time browser warning: "WebSocket is closed before the
 * connection is established." That is expected and harmless; the second connect succeeds.
 */

import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/app/store'
import { tokenManager } from '@/utils/tokenManager'
import { socketService } from '@/services/realtime/socket.service'

export function useWebSocket(): { isConnected: boolean } {
  const contextTokenFromRedux = useSelector((state: RootState) => state.auth.contextToken)
  const contextToken = contextTokenFromRedux ?? tokenManager.getToken('contextToken')

  useEffect(() => {
    if (!contextToken) {
      socketService.disconnect()
      return
    }
    socketService.reinitialize(contextToken)
    return () => {
      socketService.disconnect()
    }
  }, [contextToken])

  return { isConnected: socketService.isConnected() }
}
